import { useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from "../../../components/ui/table";
import { Badge } from "../../../components/ui/badge";
import { Tabs, TabsContent } from "../../../components/ui/tabs";
import { random } from "lodash";

interface DebtData {
  key: string;
  sr?: number;
  nature: string;
  loanType: string;
  bankName: string;
  totalLoanAmount: number;
  emi: number;
  loanStartDate: string;
  roi: number;
  outstandingAsOnDate: number;
  totalTenure: number;
}

// Format date function
const formatDate = (dateString: string | null) => {
  if (!dateString) return "-";
  const date = new Date(dateString);
  return date.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric"
  });
};

// Get initials for avatar
const getInitials = (name?: string | null): string => {
  if (!name) return "";
  if (name == null) return "";


  return name
    .split(" ")
    .map(part => part.charAt(0))
    .join("")
    .toUpperCase();
};

interface debtProfile {
  shareDebtData?: DebtData[]
  selectedDate?: Date;
}

export const DebtProfileSection: React.FC<debtProfile> = ({ shareDebtData, selectedDate }) => {

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);


  const totalLoanAmount = shareDebtData?.reduce((sum, row) => sum + (row.totalLoanAmount || 0), 0);
  const totalEmi = shareDebtData?.reduce((sum, row) => sum + (row.emi || 0), 0);
  const totalOutstanding = shareDebtData?.reduce((sum, row) => sum + (row.outstandingAsOnDate || 0), 0);

  return (
    <div className="w-full">
      <div className="flex justify-between items-center mb-6">
        <div>
          <div className="flex items-center gap-4">
            <h1 className="text-xl font-bold text-[#9e7926]">Debt Profile</h1>
            <p>( As on {selectedDate ? selectedDate.toLocaleDateString("en-GB") : "-"} )</p>
          </div>
          {/* <p className="text-gray-600">View all debt profile</p> */}
        </div>
      </div>
      {/* Tabs for Directors, Shareholding, Related Party, and Securities Allotment */}
      <Tabs value="debt-profile" className="w-full mb-6">


        <TabsContent value="debt-profile">


          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
            </div>
          ) : error ? (
            <div className="text-center py-8 text-red-600">{error}</div>
          ) : (
            <>
              {shareDebtData?.length > 0 ? (
                <div className="rounded-lg border overflow-hidden">
                  <Table className="border rounded-md">
                    <TableHeader >
                      <TableRow className="bg-gray-50">
                        <TableHead className="font-semibold" >Nature</TableHead>
                        <TableHead className="font-semibold" >Lender Name</TableHead>
                        <TableHead className="font-semibold" >Loan Type</TableHead>
                        <TableHead className="font-semibold" >Total Loan Amount</TableHead>
                        <TableHead className="font-semibold" >EMI</TableHead>
                        <TableHead className="font-semibold" >Loan Start Date</TableHead>
                        <TableHead className="font-semibold" >ROI (%)</TableHead>
                        <TableHead className="font-semibold" >Outstanding</TableHead>
                        <TableHead className="font-semibold" >Total Tenure</TableHead>
                        {/* <TableHead className="text-right">Actions</TableHead> */}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {shareDebtData?.map((debtProfile) => (
                        <TableRow key={random(1, 1000000)} className="bg-gray-50 ">
                         <TableCell>
                            <div className="flex items-center gap-3">
                              {/* <div className="h-10 w-10 rounded-full bg-gray-200 flex items-center justify-center text-gray-600">
                                {getInitials(debtProfile.nature)}
                              </div> */}
                              <div>
                                <p className="font-medium">{debtProfile.nature ?? "-"}</p>
                              </div>
                            </div>
                          </TableCell>
                          <TableCell>{debtProfile.bankName ?? "-"}</TableCell>

                          <TableCell>
                            {debtProfile.loanType ? (<Badge className={debtProfile.loanType === "Director" ?
                              "bg-blue-100 text-blue-700 hover:bg-blue-200" :
                              "bg-purple-100 text-purple-700 hover:bg-purple-200"}>
                              {debtProfile.loanType ?? "-"}
                            </Badge>) : "-" }
                            
                          </TableCell>
                          <TableCell>
                            {debtProfile.totalLoanAmount != null
                              ? debtProfile.totalLoanAmount.toLocaleString("en-IN")
                              : "-"}
                          </TableCell>
                          <TableCell>
                            {debtProfile.emi != null
                              ? debtProfile.emi.toLocaleString("en-IN")
                              : "-"}
                          </TableCell>
                          <TableCell>
                            {formatDate(debtProfile.loanStartDate) ?? "-"}
                          </TableCell>
                          <TableCell>
                            {debtProfile.roi ?? "-"}
                          </TableCell>
                          <TableCell>
                            {debtProfile.outstandingAsOnDate != null
                              ? debtProfile.outstandingAsOnDate.toLocaleString("en-IN")
                              : "-"}
                          </TableCell>
                          <TableCell>
                            {debtProfile.totalTenure != null
                              ? debtProfile.totalTenure.toLocaleString("en-IN")
                              : "-"}
                          </TableCell>
                        </TableRow>
                      ))}

                      <TableRow className="bg-gray-100 font-semibold">
                        <TableCell colSpan={3}>Total</TableCell>
                        <TableCell>{totalLoanAmount?.toLocaleString("en-IN") ?? "-"}</TableCell>
                        <TableCell>{totalEmi?.toLocaleString("en-IN") ?? "-"}</TableCell>
                        <TableCell></TableCell>
                        <TableCell></TableCell>
                        <TableCell>{totalOutstanding?.toLocaleString("en-IN") ?? "-"}</TableCell>
                        <TableCell></TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </div>
              ) : (<div className="p-6 text-center text-gray-500">
                Add the debt profile to access details of the company’s borrowings.
              </div>)}
            </>
          )}
        </TabsContent>
      </Tabs>



    </div>
  );
}
